import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { queryClient } from '@/app/query-client'
import { authApi, type AuthResponse, type LoginMode } from '@/lib/api'
import { clearStoredSession, onSessionExpired, persistSession, readStoredSession, setSessionRole, updateStoredProfile } from '@/lib/api-client'
import { demoProfile } from '@/lib/demo-data'
import type { UserProfile } from '@/lib/types'
import { reconcileRelease, setReleaseAudience } from '@/lib/release-channel'

/**
 * What a confirmed profile write may change in the live session.
 *
 * Deliberately a subset of `UserProfile` rather than the full serialized
 * Student: the extra columns the endpoint returns (`country`, `target`,
 * `calling_code`, …) still ride through the spread at runtime and reach
 * storage, but nothing in the auth layer has to know about them, so adding a
 * column to the Django serializer cannot break this contract.
 */
export interface ProfileUpdateFields extends Partial<Omit<UserProfile, 'user'>> {
  user?: Partial<UserProfile['user']>
}

/**
 * Shown on the sign-in page after the server ended a session under the
 * student. Consumer language: it says what happened and what to do, and never
 * mentions tokens, expiry or status codes.
 */
export const SESSION_ENDED_NOTICE = 'Your session ended - please sign in again.'

interface AuthState {
  profile: UserProfile | null
  loading: boolean
  demo: boolean
  /**
   * Set when the session ended on its own rather than by the student pressing
   * sign out. The sign-in page reads it; `login` clears it.
   */
  sessionNotice: string | null
  login: (email: string, password: string, mode?: LoginMode) => Promise<UserProfile>
  /** Google sign-in. Establishes exactly the same session as `login`. */
  loginWithGoogle: (idToken: string) => Promise<UserProfile>
  /**
   * Merge a server-confirmed profile change into the live session. Called
   * after `PUT /auth/profile/update/` succeeds so the header, the avatar and a
   * later reload all agree with what was saved.
   */
  applyProfileUpdate: (fields: ProfileUpdateFields) => void
  enterDemo: () => void
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthState | null>(null)
const DEMO_KEY = 'ielts-v2-demo-session'
const DEMO_ENABLED = import.meta.env.VITE_ENABLE_DEMO_SESSION === 'true'

function clearV2SessionData() {
  for (let index = sessionStorage.length - 1; index >= 0; index -= 1) {
    const key = sessionStorage.key(index)
    if (key?.startsWith('ielts-v2-')) sessionStorage.removeItem(key)
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [profile, setProfile] = useState<UserProfile | null>(null)
  const [loading, setLoading] = useState(true)
  const [demo, setDemo] = useState(false)
  const [sessionNotice, setSessionNotice] = useState<string | null>(null)

  /**
   * The server ended this session. Clear everything the signed-in app is
   * holding, in one place, so nothing is left polling.
   *
   * Setting `profile` to null is what stops the loops: `RequireAuth` redirects
   * to the sign-in page, and `RealtimeBridge`'s effect tears its socket down
   * rather than reconnecting forever. `queryClient.clear()` drops every cached
   * student query along with its refetch interval.
   */
  useEffect(
    () =>
      onSessionExpired(() => {
        sessionStorage.removeItem(DEMO_KEY)
        clearV2SessionData()
        clearStoredSession()
        setReleaseAudience('public')
        queryClient.clear()
        setProfile(null)
        setDemo(false)
        setSessionNotice(SESSION_ENDED_NOTICE)
      }),
    [],
  )

  useEffect(() => {
    if (DEMO_ENABLED && sessionStorage.getItem(DEMO_KEY) === 'true') {
      setProfile(demoProfile)
      setDemo(true)
      setLoading(false)
      return
    }

    // A stale preview session must never bypass live authentication after the
    // environment has been switched to live-backend acceptance testing.
    sessionStorage.removeItem(DEMO_KEY)

    // Match the established Vue boot contract. The backend has `/auth/me/`,
    // but it returns only a summary and cannot reconstruct the full profile or
    // role needed by the router. Login persists the authoritative profile.
    const storedProfile = readStoredSession<UserProfile>()
    if (storedProfile) {
      setSessionRole(storedProfile.user.role)
      setProfile(storedProfile)
      setReleaseAudience(storedProfile.user.role)
      // Reconcile restored admin sessions once. Student exams never reload
      // because a different frontend release was staged in the background.
      if (storedProfile.user.role === 'admin') void reconcileRelease()
    } else {
      setProfile(null)
    }
    setLoading(false)
  }, [])

  /**
   * One place where a completed sign-in becomes a session. Password and Google
   * sign-in must not drift: a socially-authenticated student has to get the
   * same stored profile, the same session role and the same cleared demo flag,
   * or the router and the WebSocket bridge will disagree about who is here.
   */
  const establishSession = useCallback((response: AuthResponse) => {
    persistSession(response.profile, {
      authToken: response.auth_token,
      refreshToken: response.refreshToken,
    })
    setSessionRole(response.profile.user.role)
    setProfile(response.profile)
    setReleaseAudience(response.profile.user.role)
    void reconcileRelease()
    setDemo(false)
    // The student has answered the notice by signing in; it must not follow
    // them into the new session.
    setSessionNotice(null)
    return response.profile
  }, [])

  const login = useCallback(async (email: string, password: string, mode: LoginMode = 'student') => {
    return establishSession(await authApi.login(email, password, mode))
  }, [establishSession])

  const loginWithGoogle = useCallback(async (idToken: string) => {
    return establishSession(await authApi.socialAuth(idToken))
  }, [establishSession])

  const applyProfileUpdate = useCallback((fields: ProfileUpdateFields) => {
    setProfile((current) => {
      if (!current) return current
      // `update_profile` returns the serialized Student, whose nested `user`
      // block is the same object login returned. Merging shallowly and keeping
      // the existing `user` avoids a partial response dropping the role the
      // router depends on.
      const merged = { ...current, ...fields, user: { ...current.user, ...(fields.user ?? {}) } }
      if (!demo) updateStoredProfile(merged)
      return merged
    })
  }, [demo])

  const enterDemo = useCallback(() => {
    if (!DEMO_ENABLED) throw new Error('Demo sessions are disabled in this environment.')
    sessionStorage.setItem(DEMO_KEY, 'true')
    setProfile(demoProfile)
    setDemo(true)
  }, [])

  /**
   * Signing out always succeeds from the student's point of view.
   *
   * The local session is torn down unconditionally; the server call is asked
   * for (`void`, not awaited) rather than waited on. Only the server can expire
   * the HttpOnly session cookies, so the request still goes out - but its
   * outcome must not decide whether the student gets signed out. Awaiting it
   * meant a request that never answers (a phone that has dropped off the
   * network holds the connection open instead of failing) left the student
   * signed in, with a sign-out control that looked broken (IELTS-506). Every
   * header is built before the first await inside `apiRequest`, so clearing
   * storage on the next line cannot strip the credentials off the call already
   * in flight, and the server half is now idempotent, so the call succeeds even
   * when the session was already dead.
   */
  const logout = useCallback(async () => {
    const ended = !demo ? authApi.logout(profile?.user.role === 'admin') : Promise.resolve()
    sessionStorage.removeItem(DEMO_KEY)
    clearV2SessionData()
    clearStoredSession()
    queryClient.clear()
    setProfile(null)
    setDemo(false)
    // A deliberate sign-out is not a session that ended under them.
    setSessionNotice(null)
    const generation = setReleaseAudience('public')
    // Local logout remains immediate; only the build switch waits for the
    // server cookie response. A newer login invalidates this reconciliation.
    void ended.then(() => reconcileRelease(generation)).catch(() => undefined)
  }, [demo, profile])

  const value = useMemo(
    () => ({ profile, loading, demo, sessionNotice, login, loginWithGoogle, applyProfileUpdate, enterDemo, logout }),
    [profile, loading, demo, sessionNotice, login, loginWithGoogle, applyProfileUpdate, enterDemo, logout],
  )
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const value = useContext(AuthContext)
  if (!value) throw new Error('useAuth must be used inside AuthProvider')
  return value
}
